import { useEffect } from 'react';
import { useLocation } from 'wouter';

import pages, { PageProps } from '../component/Pages';

function isPageLink(page: PageProps, target: HTMLAnchorElement): boolean {
  // Match exact match, eg `clients.mintel.com/something`
  if (target.pathname === page.path) {
    return true;
  }

  // Match subpaths, eg `clients.mintel.com/something/blah/blah` or `clients.mintel.com/something/`
  if (target.pathname.startsWith(`${page.path}/`)) {
    return true;
  }

  return false;
}

/**
 * Prevent full page reloads for internal links that match MFE routes
 * and use client-side navigation instead.
 */
export default function useClientSideNavigation() {
  const [, navigate] = useLocation();
  useEffect(() => {
    const navigationHandler = (event: MouseEvent) => {
      if (!(event?.target instanceof HTMLElement)) {
        return;
      }

      const target = event.target?.closest('a');
      if (target) {
        // Allow target "_blank" links to open in new tab
        if (target.target === '_blank') {
          return;
        }

        // Ignore external links
        if (target.hostname !== window.location.hostname) {
          return;
        }

        // Only handle links that match an MFE route
        if (pages.find((page) => isPageLink(page, target))) {
          // Don't client-side navigate for the content homepage
          // Even though it matches an MFE route, it's not actually a page in the app shell
          if (
            target.pathname === '/content' ||
            target.pathname === '/content/'
          ) {
            return;
          }

          // Don't client-side navigate if the target pathname is the same as the current pathname and only hash is changing
          // This will prevent issues with links pointing to a hash location within a page
          if (
            target.pathname === window.location.pathname &&
            target.hash !== window.location.hash
          ) {
            return;
          }

          event.preventDefault();
          navigate(`${target.pathname}${target.search}${target.hash}`);
        }
      }
    };

    document.addEventListener('click', navigationHandler);
    return () => {
      document.removeEventListener('click', navigationHandler);
    };
  }, [navigate]);
}
